<script lang="ts" setup>
import { ref } from "vue"
import TestcaseEmailModal from "@/modules/testcase/components/TestcaseEmailModal.vue"
import type { ReceivedEmail } from "@/services/ProjectService"

interface Props {
    emails: ReceivedEmail[]
    emailsLoaded: boolean
    addressPattern?: string
}

defineProps<Props>()
const emit = defineEmits<{
    (e: "deleteEmail", payload: { email: ReceivedEmail }): void
}>()

const previewEmail = ref<ReceivedEmail | null>(null)

function deleteEmail(email: ReceivedEmail) {
    emit("deleteEmail", { email })
}

function formatTime(iso: string) {
    return new Date(iso).toLocaleString()
}
</script>

<template lang="pug">
.emails-list.p-3
    .text-muted.mb-3(v-if="addressPattern")
        div
            span Receiving address for this test:&nbsp;
            code {{ addressPattern }}
        .small
            | Call&nbsp;
            code get_email_address()
            | &nbsp;in an EVAL action to get the exact address of a run.

    .text-muted(v-if="!emailsLoaded") Loading emails…

    .bg-very-light-grey.p-4(v-else-if="!emails.length")
        | No emails received yet for this test.

    .tu-frame.mb-2(v-for="email in emails", :key="email.id")
        .card-header.bg-white.d-flex.align-items-center(@click="previewEmail = email", style="cursor: pointer")
            .flex-grow-1.text-truncate
                strong {{ email.subject || "(no subject)" }}
                .small.text-muted
                    span {{ email.from || "unknown sender" }}
                    span.mx-2 ·
                    span {{ formatTime(email.receivedAt) }}
                    span.mx-2 ·
                    span scope {{ email.scopeType }}{{ email.scopeId }}
                    template(v-if="email.attachments && email.attachments.length")
                        span.mx-2 ·
                        span
                            i.fas.fa-paperclip.mr-1
                            | {{ email.attachments.length }}
            button.btn.btn-sm.btn-outline-secondary.ml-2(
                type="button",
                title="Preview email",
                @click.stop="previewEmail = email"
            )
                i.fas.fa-eye
            button.btn.btn-sm.btn-outline-danger.ml-2(
                type="button",
                title="Delete email",
                @click.stop="deleteEmail(email)"
            )
                i.fas.fa-trash

    testcase-email-modal(v-if="previewEmail", :email="previewEmail", @close="previewEmail = null")
</template>
